<script>
import { mapGetters, mapActions } from "vuex";
import { useVuelidate } from '@vuelidate/core'
import { required, email, decimal } from '@vuelidate/validators'

export default {
  setup () {
    return { v$: useVuelidate() }
  },
  computed: {
    ...mapGetters("auth", { getCurrentOrganisation: "getCurrentOrganisation" }),

    customer: {
      get() {
        return {
          id: this.id,
          account_manager: this.account_manager,
          archived: this.archived,
          cell_number: this.cell_number,
          contact_number: this.contact_number,
          credit_limit: this.credit_limit,
          customer_group: this.customer_group,
          email: this.email,
          first_name: this.first_name,
          last_name: this.last_name,
          is_company: this.is_company,
          is_walk_in: this.is_walk_in,
          loyalty: this.loyalty,
          marketing: this.marketing,
          notes: this.notes,
          organisation: this.getCurrentOrganisation.id,
          payment_term: this.payment_term,
          price_list: this.price_list,
          tax_number: this.tax_number,

          addresses: this.addresses,
          customer_contacts: this.customer_contacts,
          
          name: this.name,
          store_credit: this.store_credit,

          created_at: this.created_at,
          created_by: this.created_by,
          edited_at: this.edited_at,
          edited_by: this.edited_by,
        };
      },
      set(value) {
        //this.id = value.id
        this.account_manager = value.account_manager ?? this.account_manager;
        this.archived = value.archived ?? this.archived;
        this.cell_number = value.cell_number ?? this.cell_number;
        this.contact_number = value.contact_number ?? this.contact_number;
        this.credit_limit = value.credit_limit ?? this.credit_limit;
        this.customer_group = value.customer_group ?? this.customer_group;
        this.email = value.email ?? this.email;
        this.first_name = value.first_name ?? this.first_name;
        this.last_name = value.last_name ?? this.last_name;
        this.is_company = value.is_company ?? this.is_company;
        this.is_walk_in = value.is_walk_in ?? this.is_walk_in;
        this.loyalty = value.loyalty ?? this.loyalty;
        this.marketing = value.marketing ?? this.marketing;
        this.notes = value.notes ?? this.notes;
        this.organisation = value.organisation ?? this.organisation;
        this.payment_term = value.payment_term ?? this.payment_term;
        this.price_list = value.price_list ?? this.price_list;
        this.tax_number = value.tax_number ?? this.tax_number;

        this.addresses = value.addresses ?? this.addresses;
        this.customer_contacts = value.customer_contacts ?? this.customer_contacts;

        this.name = value.name ?? this.name;
        this.store_credit = value.store_credit ?? this.store_credit;

        this.created_at = value.created_at ?? this.created_at;
        this.created_by = value.created_by ?? this.created_by;
        this.edited_at = value.edited_at ?? this.edited_at;
        this.edited_by = value.edited_by ?? this.edited_by;
      },
    },

    unsavedChanges() {
      if (!this.initializedObject || !this.customer) return true;

      const keysToCompare = [
        "account_manager.id",
        "cell_number",
        "contact_number",
        "credit_limit",
        "customer_group.id",
        "email",
        "first_name",
        "last_name",
        "is_company",
        "is_walk_in",
        "loyalty",
        "marketing",
        "notes",
        "payment_term.id",
        "price_list.id",
        "tax_number",
      ];

      const hasChanges = keysToCompare.some((key) => {
        const initialValue = key.split(".").reduce((acc, part) => acc && acc[part], this.initializedObject);
        const currentValue = key.split(".").reduce((acc, part) => acc && acc[part], this.customer);
        return JSON.stringify(initialValue) !== JSON.stringify(currentValue);
      });

      if (hasChanges) return true;

      return this.customer?.addresses?.length !== this.initializedObject?.addresses?.length || 
        this.customer?.customer_contacts?.length !== this.initializedObject?.customer_contacts?.length ||
        this.customer?.addresses?.some((item) => {
          const originalItem = this.initializedObject?.addresses?.find((i) => i.id === item.id);
          if (!originalItem) return true;

          const getNestedValue = (obj, path) => {
            return path.split('.').reduce((acc, key) => acc?.[key] ?? null, obj);
          };

          const itemKeys = [
            "archived",
            "type",
            "address_one",
            "address_two",
            "city",
            "country",
            "postal_code",
            "province",
            "suburb",
          ];

          return itemKeys.some((key) => {
            const initialValue = getNestedValue(originalItem, key);
            const currentValue = getNestedValue(item, key);

            return initialValue != currentValue;
          });
        }) || 
        this.customer?.customer_contacts?.some((item) => {
          const originalItem = this.initializedObject?.customer_contacts?.find((i) => i.id === item.id);
          if (!originalItem) return true;

          const getNestedValue = (obj, path) => {
            return path.split('.').reduce((acc, key) => acc?.[key] ?? null, obj);
          };

          const itemKeys = [
            "archived",
            "name",
            "email",
            "first_name",
            "last_name",
            "purchase_limit",
            "contact_number",
          ];

          return itemKeys.some((key) => {
            const initialValue = getNestedValue(originalItem, key);
            const currentValue = getNestedValue(item, key);

            return initialValue != currentValue;
          });
        });
    },
  },
  data() {
    return {
      //id: null,
      account_manager: null,
      archived: false,
      cell_number: null,
      contact_number: null,
      credit_limit: 0,
      customer_group: null,
      email: null,
      first_name: null,
      last_name: null,
      is_company: false,
      is_walk_in: false,
      loyalty: false,
      marketing: false,
      notes: null,
      organisation: null,
      payment_term: null,
      price_list: null,
      tax_number: null,

      addresses: [],
      customer_contacts: [],

      name: null,
      store_credit: null,

      first_name: null,
      last_name: null,

      created_at: null,
      created_by: null,
      edited_at: null,
      edited_by: null,

      modelName: "Customer",

      initializedObject: null,
    };
  },
  methods: {
    ...mapActions("customers", ["fetchCustomer"]),
    ...mapActions("application", ["setIsPageLoading", "setHasUnsavedChanges"]),

    setupData(newValue) {
      this.customer = newValue;
      this.initializedObject = {
          ...newValue,
          addresses: structuredClone(newValue.addresses),
          customer_contacts: structuredClone(newValue.customer_contacts),
        };
    },

    async initialize() {
      this.setIsPageLoading(true);
      try {
        if (this.id) {
          await this.fetchCustomer({
            organisation: this.getCurrentOrganisation.id,
            id: this.id,
          }).then((res) => {
            this.setupData(res.data)
          }).catch((error) => {});
        } else if (this.value) {
          this.setupData(this.value)
        } else {
          this.account_manager = null;
          this.archived = false;
          this.cell_number = null;
          this.contact_number = this.newDetails?.contact_number || null;
          this.credit_limit = 0;
          this.customer_group = null;
          this.email = this.newDetails?.email || null;
          this.first_name = this.newDetails?.first_name || null;
          this.last_name = this.newDetails?.last_name || null;
          this.is_company = this.newDetails?.is_company || false;
          this.is_walk_in = false;
          this.loyalty = false;
          this.marketing = false;
          this.notes = null;
          this.organisation = null;
          this.payment_term = null;
          this.price_list = null;
          this.tax_number = null;

          this.addresses = [];
          this.customer_contacts = [];

          this.name = null;
          this.store_credit = null;

          // this.add_address();
        }
      } finally {
        this.setIsPageLoading(false);
      }
    },

    add_address() {
      this.addresses.push({
        //id: null,
        archived: false,
        name: null,
        address_one: null,
        address_two: null,
        city: null,
        country: "South Africa",
        latitude: null,
        longitude: null,
        organisation: null,
        postal_code: null,
        province: null,
        suburb: null,
      });

      this.$nextTick(() => {
        const addressElements = document.querySelectorAll(".address-box");
        if (addressElements.length > 0) {
          const lastAddressElement = addressElements[addressElements.length - 1];
          
          lastAddressElement.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
          });
        }
      });
    },

    add_customer_contact() {
      this.customer_contacts.push({
        archived: false,
        contact_number: null,
        customer: null,
        email: null,
        first_name: null,
        last_name: null,
        organisation: null,
        purchase_limit: null,
      });

      this.$nextTick(() => {
        const contactElements = document.querySelectorAll(".contact-box");
        if (contactElements.length > 0) {
          const lastContactElement = contactElements[contactElements.length - 1];
          
          lastContactElement.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
          });
        }
      });
    },

    remove_customer_contact(index) {
      this.customer_contacts.splice(index, 1);
    },

    remove_address(index) {
      this.addresses.splice(index, 1);
    },
  },
  mounted() {
    this.initialize();
  },
  props: {
    id: {
      type: String,
      default: null,
    },
    value: {
      type: Object,
      default(rawProps) {
        return null;
      },
    },
    newDetails: {
      type: Object,
      default(rawProps) {
        return null;
      },
    }
  },
  watch: {
    value: {
      handler(newVal) {
        this.customer = newVal;
      },
      deep: true
    },
    customer() {
      this.$emit("input", this.customer);
    },
    unsavedChanges(newVal) {
      this.setHasUnsavedChanges(newVal);
    },
  },
  validations: {
    email: { email },
    first_name: { required },
    is_company: { required },
    loyalty: { required },
    marketing: { required },
    credit_limit: { required, decimal },
  },
};
</script>

<template></template>
